import { generateClient, get, post } from "aws-amplify/api";

const client = generateClient();

// Payroll runs and PPh 21 opening balances.
//
// Every figure is computed by the payrollRun Lambda (REST API "business",
// /payroll/*). The AppSync models are read-only, so reads go through GraphQL and
// every write goes through REST. Unlike the reducer-backed actions elsewhere in
// Context these are plain async functions - they return data and throw on
// failure - because payroll state belongs to one page, not the whole app.

async function payrollData(request) {
  const { body: resBody } = await request.response;
  const res = await resBody.json();
  if (res.success === false) {
    // Same shape Amplify gives a non-2xx response, so getErrorMessages reads
    // both the same way.
    const error = new Error(res.message || "Payroll request failed");
    error.response = { body: res };
    throw error;
  }
  return res.data;
}

async function postPayroll(path, body) {
  return payrollData(
    post({ apiName: "business", path, options: body ? { body } : undefined }),
  );
}

async function getPayroll(path, queryParams) {
  return payrollData(
    get({ apiName: "business", path, options: queryParams ? { queryParams } : undefined }),
  );
}

async function listAll(query, field, variables) {
  let items = [];
  let nextToken = null;
  do {
    const res = await client.graphql({
      query,
      variables: nextToken ? { ...variables, nextToken } : variables,
    });
    const page = res.data[field];
    items = items.concat(page.items.filter(Boolean));
    nextToken = page.nextToken;
  } while (nextToken);
  return items;
}

/** The month's run that is not VOID, or null. At most one exists per period. */
export async function listPayrollRun({ businessID, period }) {
  const runs = await listAll(payrollRunsByBusinessPeriod, "payrollRunsByBusinessPeriod", {
    businessID,
    period: { eq: period },
    limit: 20,
  });
  return runs.find((run) => run.status !== "VOID") || null;
}

export async function listPayrollRunItems({ runID }) {
  return listAll(payrollRunItemsByRun, "payrollRunItemsByRun", {
    runID,
    limit: 200,
  });
}

/** Creates the month's DRAFT, or recomputes the one that exists. */
export async function createPayrollRun({ businessID, period }) {
  return postPayroll("/payroll/run", { businessID, period });
}

export async function discardPayrollRun({ id }) {
  return postPayroll(`/payroll/run/${id}/discard`);
}

/**
 * Books the DRAFT's accrual journal. `revision` is the one the user reviewed:
 * the server refuses a run recomputed since, rather than post it unseen.
 */
export async function postPayrollRun({ id, revision }) {
  return postPayroll(`/payroll/run/${id}/post`, { revision });
}

/** Pays net pay from a cash or bank account. `paidAt` is an ISO date. */
export async function payPayrollRun({ id, accountID, paidAt }) {
  return postPayroll(`/payroll/run/${id}/pay`, { accountID, paidAt });
}

/** Reverses the payment journal; the run goes back to POSTED. */
export async function cancelPayrollRunPayment({ id }) {
  return postPayroll(`/payroll/run/${id}/cancel-payment`);
}

/** Reverses the accrual journal; the month can then be run again. */
export async function voidPayrollRun({ id }) {
  return postPayroll(`/payroll/run/${id}/void`);
}

/**
 * Pays one withholding of a posted run - kind "PPH21", "BPJS_HEALTH" or
 * "BPJS_EMPLOYMENT" - for the amount the server computes. `ntpn` is PPh 21 only.
 */
export async function remitPayrollRun({ id, kind, accountID, paidAt, ntpn }) {
  const body = { kind, accountID, paidAt };
  if (ntpn) body.ntpn = ntpn;
  return postPayroll(`/payroll/run/${id}/remit`, body);
}

/** Reverses one withholding's payment journal. */
export async function cancelPayrollRunRemittance({ id, kind }) {
  return postPayroll(`/payroll/run/${id}/cancel-remittance`, { kind });
}

/**
 * One journal the run booked, with its legs, for TransactionDetailModal. The
 * legs come back in index order; they are put the way a journal reads, debits
 * first, then by account code.
 */
export async function getPayrollJournal({ id }) {
  const res = await client.graphql({ query: getPayrollJournalQuery, variables: { id } });
  const journal = res.data.getBusinessAccountingTransaction;
  if (!journal?.accounts?.items) return journal;
  const items = [...journal.accounts.items].sort(
    (a, b) =>
      (b.debit > 0) - (a.debit > 0) ||
      (a.account?.code || "").localeCompare(b.account?.code || ""),
  );
  return { ...journal, accounts: { ...journal.accounts, items } };
}

/**
 * Prints payslips of a POSTED or PAID run - one employee's, or everyone's in
 * one PDF - into the caller's private storage. Premium only: a free member
 * gets a 403 with code PREMIUM_REQUIRED. Resolves to { key, fileName }.
 */
export async function exportPayslips({ runId, employeeId }) {
  return getPayroll(
    `/payroll/run/${runId}/payslip`,
    employeeId ? { employeeId } : undefined,
  );
}

export async function listPayrollOpeningBalances({ businessID, year }) {
  return listAll(
    payrollOpeningBalancesByBusinessYear,
    "payrollOpeningBalancesByBusinessYear",
    { businessID, year: { eq: year }, limit: 200 },
  );
}

/** Upsert: one opening balance per employee per year. */
export async function savePayrollOpeningBalance(payload) {
  return postPayroll("/payroll/opening-balance", payload);
}

export async function deletePayrollOpeningBalance({ id }) {
  return postPayroll(`/payroll/opening-balance/${id}/delete`);
}

const PAYROLL_RUN_FIELDS = /* GraphQL */ `
  id
  businessID
  period
  status
  revision
  calcVersion
  employeeCount
  totalBaseSalary
  totalGross
  totalTaxAllowance
  totalPph21
  totalEmployeeDeductions
  totalEmployerContributions
  totalNetPay
  skipped {
    employeeID
    employeeName
    reason
  }
  computedAt
  postedAt
  accrualTransactionID
  netPayAccountID
  paidAt
  paymentAccountID
  paymentTransactionID
  pph21PaidAt
  pph21AccountID
  pph21TransactionID
  pph21Ntpn
  bpjsHealthPaidAt
  bpjsHealthAccountID
  bpjsHealthTransactionID
  bpjsEmploymentPaidAt
  bpjsEmploymentAccountID
  bpjsEmploymentTransactionID
  createdAt
  updatedAt
`;

const PAYROLL_LINE_FIELDS = /* GraphQL */ `
  sourceID
  name
  type
  amount
  isTaxable
  reduceIncome
`;

const PAYROLL_RUN_ITEM_FIELDS = /* GraphQL */ `
  id
  runID
  businessID
  employeeID
  period
  status
  revision
  calcVersion
  employeeName
  employeeCode
  marriageStatus
  dependent
  ptkpStatus
  terCategory
  taxMethod
  riskLevel
  baseSalary
  attendanceDeduction
  isAttendanceDeductionCapped
  allowances {
    ${PAYROLL_LINE_FIELDS}
  }
  deductions {
    ${PAYROLL_LINE_FIELDS}
  }
  employerHealth
  employerJkk
  employerJkm
  employerJht
  employerJp
  taxableAllowances
  grossSalary
  positionCost
  pensionDeduction
  terRate
  taxAllowance
  pph21
  isLastTaxPeriod
  yearlyTaxableIncome
  yearlyTax
  ytdProjectedMonths
  employeeDeductions
  netPay
  computedAt
`;

export const payrollRunsByBusinessPeriod = /* GraphQL */ `
  query PayrollRunsByBusinessPeriod(
    $businessID: ID
    $period: ModelStringKeyConditionInput
    $limit: Int
    $nextToken: String
  ) {
    payrollRunsByBusinessPeriod(
      businessID: $businessID
      period: $period
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ${PAYROLL_RUN_FIELDS}
      }
      nextToken
    }
  }
`;

export const payrollRunItemsByRun = /* GraphQL */ `
  query PayrollRunItemsByRun($runID: ID, $limit: Int, $nextToken: String) {
    payrollRunItemsByRun(runID: $runID, limit: $limit, nextToken: $nextToken) {
      items {
        ${PAYROLL_RUN_ITEM_FIELDS}
      }
      nextToken
    }
  }
`;

export const getPayrollJournalQuery = /* GraphQL */ `
  query GetPayrollJournal($id: ID!) {
    getBusinessAccountingTransaction(id: $id) {
      id
      businessID
      type
      description
      referenceNumber
      payrollRunID
      total
      paid
      createdAt
      accounts {
        items {
          id
          accountID
          debit
          credit
          account {
            id
            code
            name
          }
        }
      }
    }
  }
`;

export const payrollOpeningBalancesByBusinessYear = /* GraphQL */ `
  query PayrollOpeningBalancesByBusinessYear(
    $businessID: ID
    $year: ModelIntKeyConditionInput
    $limit: Int
    $nextToken: String
  ) {
    payrollOpeningBalancesByBusinessYear(
      businessID: $businessID
      year: $year
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        businessID
        employeeID
        year
        throughMonth
        marriageStatus
        dependent
        grossIncome
        positionCost
        pensionDeduction
        taxWithheld
        note
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;
